import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { AccountData } from '../account.model';
import { Subscription } from 'rxjs';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AccountActivationStage,
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService,
  DateHelper,
  DelinquentStatus,
  EnumHelper,
  FormHelper,
  LoanInfoDto,
  LoanStatus,
  MessageService,
  SimpleDialogService,
  CommonActionKey,
} from 'common';
import * as _ from 'lodash';
import { ProductData } from '../../product/product.model';
import { AccountService } from '../account.service';

@UntilDestroy()
@Component({
  selector: 'ifb-account-profile-status-details',
  templateUrl: './account-profile-status-details.component.html',
  styleUrls: ['./account-profile-status-details.component.scss'],
  standalone: false,
})
export class AccountProfileStatusDetailsComponent implements OnInit {
  private saveSub: Subscription;
  form: UntypedFormGroup;
  data: AccountStateDetailsComponentData;
  logs: any;

  accountStatusOptions: LoanStatus[] = [];
  private statuses = EnumHelper.getNamesAndValues(LoanStatus);

  delinquentStatuses = EnumHelper.getNamesAndValues(DelinquentStatus);
  minFrozenUntilDate: Date;
  minFreezeForDays = 1;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly messageService: MessageService,
    private readonly datePipe: DatePipe,
    private readonly simpleDialogService: SimpleDialogService,
  ) {
    this.formInit();

    this.appBarActionsService.actions = [
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      {
        id: 'save',
        label: 'Save',
        disabled: !this.form.dirty,
        buttonType: 'submit',
        buttonAppearance: 'flat',
        buttonColor: 'primary',
      },
    ];
  }

  ngOnInit() {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.dataInit.bind(this));

    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));

    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(this.actionUpdate.bind(this));

    this.logs = EnumHelper.getNamesAndValues(this.data.account.activationStageLog);

    this.onChanges();
  }

  onChanges(): void {
    this.form.valueChanges.subscribe((val) => {
      if (this.form.dirty) {
        this.appBarActionsService.enable('save', true);
      }
    });
  }

  actionDispatch(action: AppBarAction) {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  private actionUpdate() {
    this.appBarActionsService.label('save', 'Save');
  }

  formatPascalCase(text: string): string {
    const result = text.replace(/([A-Z])/g, ' $1').toLowerCase();
    return result.charAt(1).toUpperCase() + result.slice(2);
  }

  cancel() {
    this.appPageService.back();
  }

  private formInit() {
    this.minFrozenUntilDate = new Date();
    this.minFrozenUntilDate.setDate(this.minFrozenUntilDate.getDate() + this.minFreezeForDays);
    this.minFrozenUntilDate.setHours(0, 0, 0, 0);

    this.form = this.formBuilder.group({
      status: [undefined, Validators.required],
      frozenUntil: undefined,
      freezeForDays: [undefined, Validators.min(this.minFreezeForDays)],
      delinquentStatus: [undefined, Validators.required],
      activationStage: [{ value: undefined, disabled: true }],
      createdOn: new UntypedFormControl(undefined, { validators: null, updateOn: 'blur' }),
      agreementSignatureDate: new UntypedFormControl(undefined, { validators: null, updateOn: 'blur' }),
      renewalDate: new UntypedFormControl(undefined, { validators: null, updateOn: 'blur' }),
      fullAmortizationStartDateUtc: new UntypedFormControl(undefined, { validators: null, updateOn: 'blur' }),
      updatedOn: new UntypedFormControl(undefined, { validators: null, updateOn: 'blur' }),
    });
  }

  dataInit(data: AccountStateDetailsComponentData) {
    this.data = data;
    this.appBarTitleService.title = 'Status and activation';

    this.form.reset({
      status: this.data.account.status !== null ? this.data.account.status : '',
      frozenUntil: this.data.account.frozenUntil !== null ? this.data.account.frozenUntil : '',
      freezeForDays: this.data.account.freezeForDays !== null ? this.data.account.freezeForDays : '',
      delinquentStatus:
        this.data.account.delinquentStatus !== null ? this.data.account.delinquentStatus : DelinquentStatus.Current,
      activationStage: this.data.account.activationStage
        ? EnumHelper.getNameFromValue(AccountActivationStage, this.data.account.activationStage)
        : '',
      createdOn: this.data.account.createdOn || '',
      agreementSignatureDate: this.data.account.openedOn || '',
      fullAmortizationStartDateUtc: DateHelper.getUTCDateAsDateLocal(this.data.account.fullAmortizationStartDateUtc),
      renewalDate: this.data.account.renewalDate || '',
      updatedOn: this.data.account.updatedOn || '',
    });

    const product = data.products.find((p) => p.id === data.account.productId);
    // @ts-ignore - TODO: Refactor to align with "@types/lodash" typings - RYNO-12167
    this.accountStatusOptions = _.flatMap(
      this.statuses.filter((i) => _.includes(product.allowedLoanStatuses, i.value)),
    );

    this.form.markAsPristine();
    this.actionUpdate();
  }

  clear(fc: string, form: UntypedFormGroup) {
    const formControl = form.get(fc);
    formControl.reset();
    formControl.markAsTouched();
    formControl.markAsDirty();
    form.markAsDirty();
  }

  save() {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub) return;
    if (!this.form.valid) return;

    const accountStatus = _.startCase(EnumHelper.getNameFromValue(LoanStatus, this.data.account.status));
    const accountStatusUpdated = _.startCase(EnumHelper.getNameFromValue(LoanStatus, this.form.controls.status.value));
    const delinquentStatus = _.startCase(
      EnumHelper.getNameFromValue(DelinquentStatus, this.data.account.delinquentStatus),
    );
    const delinquentStatusUpdated = _.startCase(
      EnumHelper.getNameFromValue(DelinquentStatus, this.form.controls.delinquentStatus.value),
    );
    const freezeForDays = this.data.account.freezeForDays || 0;
    const freezeForDaysUpdated = this.form.controls.freezeForDays.value || 0;
    const fullAmortizationStartDateUtc = this.data.account.fullAmortizationStartDateUtc;
    const fullAmortizationStartDateUtcUpdated =
      this.form.controls.fullAmortizationStartDateUtc.value === ''
        ? null
        : this.form.controls.fullAmortizationStartDateUtc.value;
    let updateMessage = `You are changing ${this.data.account.loanNumber}’s`;

    if (accountStatus !== accountStatusUpdated)
      updateMessage += ` status from ${accountStatus} to ${accountStatusUpdated}`;
    if (delinquentStatus !== delinquentStatusUpdated) {
      if (accountStatus !== accountStatusUpdated) updateMessage += ',';
      updateMessage += ` delinquent status from ${delinquentStatus} to ${delinquentStatusUpdated}`;
    }
    if (freezeForDays !== freezeForDaysUpdated)
      updateMessage += `, freeze for days from ${freezeForDays} to ${freezeForDaysUpdated}`;

    if (fullAmortizationStartDateUtc !== fullAmortizationStartDateUtcUpdated)
      updateMessage += `, full amortization start date from ${this.datePipe.transform(fullAmortizationStartDateUtc, 'mediumDate') || '[empty]'}
        to ${this.datePipe.transform(fullAmortizationStartDateUtcUpdated, 'mediumDate') || '[empty]'}`;

    updateMessage += '. <br>Are you sure you want to proceed?';

    this.simpleDialogService.openSimpleConfirmDialog(updateMessage).subscribe((result: CommonActionKey) => {
      if (result === CommonActionKey.Confirm)
        this.saveSub = this.accountService
          .updateAccountStatus(this.data.account.id, {
            status: this.form.controls.status.value,
            delinquentStatus: this.form.controls.delinquentStatus.value,
            freezeForDays: this.form.controls.freezeForDays.value,
            fullAmortizationStartDate: DateHelper.getLocalDateAsUTC(
              this.form.controls.fullAmortizationStartDateUtc.value,
            ),
          })
          .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
    });
  }

  private saveSubClear() {
    if (this.saveSub) this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveErrorHandler(error: any) {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSuccessHandler() {
    this.saveSubClear();
    this.accountService
      .get(this.data.account.id)
      .pipe(untilDestroyed(this))
      .subscribe((data) => this.accountService.onCurrentLoanChanged.next(data));
    this.appPageService.back();
  }
}

export interface AccountStateDetailsComponentData {
  account: AccountData;
  accountInfo: LoanInfoDto;
  products: ProductData[];
}
